import { useState, useEffect, useMemo } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Crown, Sparkles, Rocket, Search, History, Loader2, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

// Phase 89A — Super Admin Tier Manager.
// Centralizes Starter/Pro/Legend tier flips so we never have to SSH and mongosh
// our way through a sales upgrade. Writes an immutable audit trail for compliance.

const TIER_THEME = {
  starter: { label: 'Starter', cls: 'bg-slate-100 text-slate-700 border-slate-300', Icon: Sparkles },
  pro:     { label: 'Pro',     cls: 'bg-blue-100 text-blue-700 border-blue-300',     Icon: Rocket },
  legend:  { label: 'Legend',  cls: 'bg-gradient-to-r from-amber-400 to-pink-500 text-white border-0', Icon: Crown },
};

export default function SuperAdminTiers() {
  const [data, setData] = useState(null);
  const [audit, setAudit] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [search, setSearch] = useState('');
  const [confirmFlip, setConfirmFlip] = useState(null); // {academy, from, to}
  const [reason, setReason] = useState('');

  const refresh = async () => {
    try {
      const [t, a] = await Promise.all([
        api.get('/super-admin/tiers').then(r => r.data),
        api.get('/super-admin/tiers/audit').then(r => r.data),
      ]);
      setData(t);
      setAudit(a.audit || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const filteredAcademies = useMemo(() => {
    if (!data?.academies) return [];
    if (!search.trim()) return data.academies;
    const q = search.trim().toLowerCase();
    return data.academies.filter(a =>
      (a.name || '').toLowerCase().includes(q) ||
      (a.city || '').toLowerCase().includes(q) ||
      (a.owner_email || '').toLowerCase().includes(q)
    );
  }, [data, search]);

  const startFlip = (academy, toTier) => {
    if ((academy.subscription_tier || 'starter') === toTier) return;
    setConfirmFlip({ academy, from: academy.subscription_tier || 'starter', to: toTier });
    setReason('');
  };

  const confirmAndFlip = async () => {
    if (!confirmFlip) return;
    setBusyId(confirmFlip.academy.id);
    try {
      await api.put(`/super-admin/academies/${confirmFlip.academy.id}/tier`, {
        tier: confirmFlip.to,
        reason,
      });
      toast.success(`${confirmFlip.academy.name} → ${TIER_THEME[confirmFlip.to].label}`);
      setConfirmFlip(null);
      setReason('');
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-6 space-y-4 max-w-6xl mx-auto">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-32 w-full rounded-xl" />
        <Skeleton className="h-72 w-full rounded-xl" />
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-4 lg:p-6 space-y-4 max-w-6xl mx-auto" data-testid="tiers-page">
      {/* Header */}
      <header>
        <h1 className="text-xl lg:text-2xl font-display font-bold flex items-center gap-2"><Crown className="h-5 w-5 text-amber-500" /> Tier Manager</h1>
        <p className="text-xs text-muted-foreground mt-0.5">Flip any academy between Starter, Pro, and Legend. Every change is logged.</p>
      </header>

      {/* Tier counts */}
      <div className="grid grid-cols-3 gap-3">
        {Object.entries(TIER_THEME).map(([key, theme]) => {
          const count = data.counts?.[key] ?? 0;
          const Icon = theme.Icon;
          return (
            <Card key={key} data-testid={`tier-count-${key}`}>
              <CardContent className="p-3 flex items-center gap-3">
                <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${key === 'legend' ? 'bg-gradient-to-br from-amber-400 to-pink-500 text-white' : key === 'pro' ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-700'}`}>
                  <Icon className="h-4 w-4" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-bold">{theme.label}</p>
                  <p className="text-xl font-display font-bold tabular-nums">{count}</p>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Academies table */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between gap-3">
          <CardTitle className="text-base">All Academies</CardTitle>
          <div className="relative w-56">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by name, city, owner…"
              className="h-8 pl-7 text-xs"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="tiers-search"
            />
          </div>
        </CardHeader>
        <CardContent>
          {filteredAcademies.length === 0 ? (
            <div className="py-10 text-center text-sm text-muted-foreground" data-testid="tiers-empty">
              {search.trim() ? `No academies match "${search}"` : 'No academies yet.'}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Academy</TableHead>
                    <TableHead className="hidden md:table-cell">City</TableHead>
                    <TableHead>Current Tier</TableHead>
                    <TableHead>Flip To</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredAcademies.map(ac => {
                    const tier = ac.subscription_tier || 'starter';
                    const theme = TIER_THEME[tier] || TIER_THEME.starter;
                    return (
                      <TableRow key={ac.id} data-testid={`tier-row-${ac.id}`}>
                        <TableCell>
                          <p className="font-medium text-sm">{ac.name}</p>
                          <p className="text-[10px] text-muted-foreground truncate max-w-[200px]">{ac.owner_email}</p>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-xs text-muted-foreground">{ac.city || '—'}</TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] border ${theme.cls}`} data-testid={`tier-current-${ac.id}`}>{theme.label}</Badge>
                        </TableCell>
                        <TableCell>
                          <Select
                            value={tier}
                            disabled={busyId === ac.id}
                            onValueChange={(v) => startFlip(ac, v)}
                          >
                            <SelectTrigger className="h-7 text-xs w-32" data-testid={`tier-select-${ac.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {data.allowed_tiers.map(t => (
                                <SelectItem key={t} value={t}>
                                  <span className="flex items-center gap-1.5">{TIER_THEME[t].label}</span>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Audit log */}
      <Card data-testid="tiers-audit">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><History className="h-4 w-4" /> Recent tier changes</CardTitle>
        </CardHeader>
        <CardContent>
          {audit.length === 0 ? (
            <p className="text-xs text-muted-foreground py-4 text-center">No tier changes yet.</p>
          ) : (
            <div className="space-y-1.5">
              {audit.map(row => (
                <div key={row.id} className="flex items-center justify-between text-xs px-2 py-1.5 rounded hover:bg-muted/40" data-testid={`tiers-audit-row-${row.id}`}>
                  <div className="min-w-0">
                    <p className="font-medium truncate">{row.academy_name}</p>
                    <p className="text-[10px] text-muted-foreground truncate">{row.changed_by_email}{row.reason ? ` · "${row.reason}"` : ''}</p>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 ml-2">
                    <Badge variant="outline" className="text-[10px]">{TIER_THEME[row.from_tier]?.label || row.from_tier}</Badge>
                    <span className="text-muted-foreground">→</span>
                    <Badge className={`text-[10px] border ${(TIER_THEME[row.to_tier] || TIER_THEME.starter).cls}`}>{TIER_THEME[row.to_tier]?.label || row.to_tier}</Badge>
                    <span className="text-[9px] text-muted-foreground tabular-nums ml-1">{new Date(row.at).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirm flip dialog */}
      <Dialog open={!!confirmFlip} onOpenChange={() => !busyId && setConfirmFlip(null)}>
        <DialogContent className="max-w-md" data-testid="tier-confirm-dialog">
          {confirmFlip && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><AlertCircle className="h-5 w-5 text-amber-500" /> Confirm tier change</DialogTitle>
              </DialogHeader>
              <div className="space-y-3">
                <p className="text-sm">
                  <span className="font-semibold">{confirmFlip.academy.name}</span> will move from{' '}
                  <Badge variant="outline" className="text-[10px] mx-1">{TIER_THEME[confirmFlip.from]?.label}</Badge>
                  <span>→</span>
                  <Badge className={`text-[10px] mx-1 ${TIER_THEME[confirmFlip.to]?.cls}`}>{TIER_THEME[confirmFlip.to]?.label}</Badge>
                </p>
                <Textarea
                  placeholder="Reason (optional — but recommended for the audit log)"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  data-testid="tier-confirm-reason"
                />
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" disabled={!!busyId} onClick={() => setConfirmFlip(null)}>Cancel</Button>
                  <Button className="flex-1 bg-gradient-to-r from-amber-500 to-pink-500 text-white" disabled={!!busyId} onClick={confirmAndFlip} data-testid="tier-confirm-btn">
                    {busyId ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : null}
                    Confirm change
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
